import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import circle from "@turf/circle";
import mapboxgl from "mapbox-gl";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import utils from "../../../app/js/utils";
import CustomDrawControls from "./CustomDrawControls";
import DirectSelectPolygon from "./DirectSelectPolygonMode";
import DirectSelectRectangle from "./DirectSelectRectangleMode";
import { mapDrawStyles } from "./MapDrawStyles";

const useDrawingTool = (props) => {
    const {
        map,
        disableDraw,
        mapReady,
        searchByArea,
        handleSearchCleared,
        clearFilters,
        mapCrumbCleared,
        savedPolygon,
        updateSearchPolygon,
        savedMapDetails,
        savedSearchRun,
    } = props;

    const drawRef = useRef(null);
    const lastDrawnFeatureIdRef = useRef(null);
    const lastDrawnRef = useRef(null);
    const radiusMilesRef = useRef(null);
    const isDraggingRef = useRef(false);
    const popupRef = useRef(null);
    const coordinatesRef = useRef(null);
    const drawModeRef = useRef(null);
    const customControlsRef = useRef(null);
    const dispatch = useDispatch();

    const onMapLoad = () => {
        loadDrawingTools();
        map.on("draw.create", handleSearch);
        map.on("draw.update", handleSearchUpdate);
        map.on("draw.delete", handleDelete);
        map.on("draw.undo", handleUndo);
        map.on("draw.selectionchange", handleSelectionChange);
        map.on("style.load", handleStyleChange);
    };

    const unmountDrawingTools = () => {
        if (map._removed) return;
        try {
            const styleElement = document.getElementById("drawing-controls-style");
            if (styleElement) {
                styleElement.remove();
            }
            if (map && drawRef.current) {
                map.removeControl(drawRef.current);
            }
            if (map && customControlsRef.current) {
                map.removeControl(customControlsRef.current);
            }
        } catch (e) {
            dispatch(utils.operations.snackbar(true, "error", e.message));
        } finally {
            drawRef.current = null;
            customControlsRef.current = null;
        }
    };

    useEffect(() => {
        if (!mapReady || !map) return;

        if (map.loaded()) {
            onMapLoad();
        } else {
            map.on("load", onMapLoad);
        }

        return () => {
            unmountDrawingTools();
        };
    }, [mapReady]);

    useEffect(() => {
        if (!map?.loaded() || !drawRef.current) return;
        if (!disableDraw) {
            addDrawingToolsToMap();
            restoreFromRedux();
        } else {
            popupRef.current?.remove();
            removeRadialCircle();
            map.removeControl(drawRef.current);
            customControlsRef.current && map.removeControl(customControlsRef.current);
        }
    }, [disableDraw]);

    useEffect(() => {
        if (mapCrumbCleared) {
            removeRadialCircle();
            popupRef.current?.remove();
            removeUndoMarkerFromMap();
            radiusMilesRef.current = null;
            lastDrawnRef.current = null;
            lastDrawnFeatureIdRef.current = null;
            drawModeRef.current = null;
            drawRef.current && map?.removeControl(drawRef.current);
            customControlsRef.current && map?.removeControl(customControlsRef.current);
            addDrawingToolsToMap();
        }
    }, [mapCrumbCleared]);

    useEffect(() => {
        if (savedSearchRun && mapReady) {
            if (lastDrawnFeatureIdRef.current) {
                removeRadialCircle();
                drawRef.current && drawRef.current.delete(lastDrawnFeatureIdRef.current);
                lastDrawnRef.current = null;
                lastDrawnFeatureIdRef.current = null;
            }
        }
        restoreFromSavedSearch();
    }, [savedSearchRun, savedMapDetails, mapReady]);

    const loadDrawingTools = () => {
        const modes = MapboxDraw.modes;
        modes.draw_rectangle = DrawRectangle;
        modes.direct_select_rectangle = DirectSelectRectangle;
        modes.direct_select_polygon = DirectSelectPolygon;
        drawRef.current = new MapboxDraw({
            modes: modes,
            displayControlsDefault: false,
            controls: {},
            styles: mapDrawStyles,
        });

        if (!disableDraw) {
            addDrawingToolsToMap();
            restoreFromRedux();
        }
    };

    const restoreFromSavedSearch = () => {
        if (
            !savedSearchRun ||
            !savedMapDetails ||
            Object.keys(savedMapDetails).length === 0 ||
            !drawRef.current ||
            !mapReady
        )
            return;

        const { areaPoints, radiusInMiles, mode } = savedMapDetails;
        const isCircle = mode === "simple_select" && radiusInMiles;
        const coordinates = isCircle ? [areaPoints[0].lon, areaPoints[0].lat] : [areaPoints.map((p) => [p.lon, p.lat])];
        const feature = {
            id: `saved-${Date.now()}`,
            type: "Feature",
            properties: isCircle ? { radiusMiles: radiusInMiles } : {},
            geometry: {
                type: isCircle ? "Point" : "Polygon",
                coordinates: coordinates,
            },
        };

        cleanupPreviousFeature(feature);
        drawRef.current.add(feature);
        drawModeRef.current = mode || null;
        drawRef.current.changeMode(mode, { featureId: feature.id });
        lastDrawnFeatureIdRef.current = feature.id;
        lastDrawnRef.current = feature;

        if (isCircle) {
            radiusMilesRef.current = radiusInMiles;
            coordinatesRef.current = coordinates;
            drawCircleOnMap(coordinates, radiusInMiles);
        }

        const viewport = { center: map.getCenter(), zoom: map.getZoom() };
        updateSearchPolygon(
            {
                ...feature,
                properties: { ...feature.properties, radiusInMiles, savedSearch: true },
                mode: drawRef.current?.getMode(),
                viewport,
            },
            false
        );
    };

    const handleStyleChange = () => {
        if (radiusMilesRef.current && coordinatesRef.current) {
            drawCircleOnMap(coordinatesRef.current, radiusMilesRef.current);
        }
    };

    const restoreFromRedux = () => {
        if (!savedPolygon?.geometry || !drawRef.current) return;
        drawRef.current.add(savedPolygon);
        drawModeRef.current = savedPolygon?.mode || null;
        drawRef.current?.changeMode(savedPolygon?.mode, { featureId: savedPolygon?.id });
        lastDrawnFeatureIdRef.current = savedPolygon.id;
        lastDrawnRef.current = savedPolygon;
        if (savedPolygon.geometry.type === "Point" && savedPolygon.properties?.radiusMiles) {
            radiusMilesRef.current = savedPolygon.properties.radiusMiles;
            coordinatesRef.current = savedPolygon.geometry.coordinates;
            drawCircleOnMap(coordinatesRef.current, radiusMilesRef.current);
        }
    };

    const addDrawingToolsToMap = () => {
        if (!map || !drawRef.current) return;
        const existing = document.querySelector(".mapbox-gl-draw_ctrl-draw-btn");
        if (existing) return;
        customControlsRef.current = new CustomDrawControls();
        map.addControl(customControlsRef.current, "top");
        map.addControl(drawRef.current, "top");
        customControlsRef.current.setDraw(drawRef.current);
    };

    const cleanupPreviousFeature = (feature) => {
        if (lastDrawnFeatureIdRef.current && lastDrawnFeatureIdRef.current !== feature?.id) {
            drawRef.current.delete(lastDrawnFeatureIdRef.current);
        }
        lastDrawnFeatureIdRef.current = feature?.id;
        lastDrawnRef.current = feature;
        removeRadialCircle();
    };

    const removeUndoMarkerFromMap = () => {
        const undoBtn = document.querySelector('button[title="Undo"]');
        undoBtn?.closest(".mapboxgl-marker")?.remove();
    };

    const removeRadialCircle = () => {
        if (map?.getSource("radial-search-circle")) {
            map.removeLayer("radial-search-circle-fill");
            map.removeLayer("radial-search-circle-stroke");
            map.removeSource("radial-search-circle");
        }
    };

    const handleSearch = (e) => {
        const feature = e.features[0];
        if (!feature) return;
        const type = feature.geometry?.type;
        coordinatesRef.current = feature.geometry?.coordinates;

        if (type === "Polygon") {
            drawModeRef.current =
                drawRef.current?.getMode() === "draw_rectangle" ? "direct_select_rectangle" : "direct_select_polygon";
            setTimeout(() => {
                drawRef.current?.changeMode(drawModeRef.current, { featureId: feature.id });
                handleSearchByPolygon(coordinatesRef.current[0], feature);
            }, 0);
        } else if (type === "Point") {
            drawModeRef.current = "simple_select";
            const popupContent = document.createElement("div");
            popupContent.style.cssText = "display:flex;flex-direction:column;gap:6px";
            popupContent.innerHTML = `<div style="display:flex;align-items:center;gap:6px">
                                        <label style="font-size:13px">Radius (mi):</label>
                                        <input type="number" id="radius-input" min="0.1" step="0.1" value="1" style="width:50px;font-size:13px;padding:2px 4px"/>
                                      </div>
                                      <div style="display:flex;align-items:center;gap:6px">
                                        <button id="radius-cancel" style="width:50%;background:#2C99D2;color:#fff;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:13px">Cancel</button>
                                        <button id="radius-submit" style="width:50%;background:#2C99D2;color:#fff;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:13px">Search</button>
                                      </div>`;
            popupRef.current?.remove();
            popupRef.current = new mapboxgl.Popup({
                closeOnClick: false,
                closeButton: false,
                className: "radius-popup",
            })
                .setLngLat([coordinatesRef.current[0], coordinatesRef.current[1]])
                .setDOMContent(popupContent)
                .addTo(map);

            const submitRadius = () => {
                const radius = parseFloat(popupContent.querySelector("#radius-input").value);
                radiusMilesRef.current = radius;
                popupRef.current.remove();
                handleRadialSearch(coordinatesRef.current, radius, feature);
            };

            popupContent.querySelector("#radius-submit").addEventListener("click", submitRadius);
            popupContent.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    submitRadius();
                }
            });

            popupContent.querySelector("#radius-cancel").addEventListener("click", () => {
                popupRef.current.remove();
                drawRef.current.delete(feature.id);
            });
        }
    };

    const handleSearchUpdate = (e) => {
        if (!isDraggingRef.current) {
            isDraggingRef.current = true;
            map.once("mouseup", () => {
                isDraggingRef.current = false;
                const feature = e.features[0];
                const type = feature?.geometry?.type;
                coordinatesRef.current = feature?.geometry?.coordinates;
                if (type === "Polygon") {
                    handleSearchByPolygon(coordinatesRef.current[0], feature);
                } else if (type === "Point") {
                    popupRef.current?.setLngLat([coordinatesRef.current[0], coordinatesRef.current[1]]);
                    handleRadialSearch(coordinatesRef.current, radiusMilesRef.current, feature);
                }
            });
        }
    };

    const handleSelectionChange = (e) => {
        if (e.features.length !== 1 || !drawModeRef.current) return;
        const feature = e.features[0];
        if (feature.id !== lastDrawnFeatureIdRef.current) return;
        if (drawRef.current?.getMode() === drawModeRef.current) return;
        setTimeout(() => drawRef.current?.changeMode(drawModeRef.current, { featureId: feature.id }), 0);
    };

    const handleDelete = () => {
        popupRef.current?.remove();
        radiusMilesRef.current = null;
        removeRadialCircle();
        drawRef.current.delete(lastDrawnFeatureIdRef.current);
        lastDrawnRef.current = null;
        lastDrawnFeatureIdRef.current = null;
        updateSearchPolygon(null);
        handleSearchCleared(true);
        clearFilters();
    };

    const handleUndo = (e) => {
        const feature = e.features[0];
        if (!feature) return;
        coordinatesRef.current = feature.geometry?.coordinates;
        handleSearchByPolygon(coordinatesRef.current[0], feature);
    };

    const handleSearchByPolygon = (coordinates, feature) => {
        const latlng = coordinates?.map(([lng, lat]) => ({ lat, lon: lng }));
        if (latlng && latlng.length > 2) {
            searchByArea(latlng, drawRef.current?.getMode());
        }
        const viewport = { center: map.getCenter(), zoom: map.getZoom() };
        cleanupPreviousFeature(feature);
        updateSearchPolygon({
            ...feature,
            properties: { ...feature.properties, savedSearch: false },
            mode: drawRef.current?.getMode(),
            viewport,
        });
    };

    const handleRadialSearch = (center, radiusMiles, feature) => {
        if (!center || center.length < 2 || !radiusMiles) return;
        searchByArea([{ lon: center[0], lat: center[1] }], drawRef.current?.getMode(), radiusMiles);
        cleanupPreviousFeature(feature);
        drawCircleOnMap(center, radiusMiles);
        const viewport = { center: map.getCenter(), zoom: map.getZoom() };
        updateSearchPolygon({
            ...feature,
            properties: { ...feature.properties, radiusMiles, savedSearch: false },
            mode: drawRef.current?.getMode(),
            viewport,
        });
    };

    const drawCircleOnMap = (center, radiusMiles) => {
        const circleFeature = circle(center, radiusMiles, { units: "miles" });
        if (map.getSource("radial-search-circle")) {
            map.getSource("radial-search-circle").setData(circleFeature);
        } else {
            map.addSource("radial-search-circle", { type: "geojson", data: circleFeature });
            map.addLayer({
                id: "radial-search-circle-fill",
                type: "fill",
                source: "radial-search-circle",
                paint: { "fill-color": "#000000", "fill-opacity": 0.1 },
            });
            map.addLayer({
                id: "radial-search-circle-stroke",
                type: "line",
                source: "radial-search-circle",
                paint: { "line-color": "#000000", "line-width": 2 },
            });
        }
    };
};

export default useDrawingTool;
